import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Work } from '@/mocks/catalog';
import { genreOptions, regionOptions, sortOptions, yearOptions } from '@/mocks/catalog';

export interface CatalogQuery {
  genre: string;
  region: string;
  year: string;
  sort: string;
}

interface FilterPanelProps {
  resultCount: number;
  value: CatalogQuery;
  onChange: (next: CatalogQuery) => void;
  onReset: () => void;
}

const group = (title: string, icon: string, options: string[], active: string, onPick: (v: string) => void) => (
  <div>
    <h3 className="flex items-center gap-1.5 text-xs font-semibold text-foreground-950">
      <i className={`${icon} text-accent-600`}></i>
      {title}
    </h3>
    <div className="mt-2.5 flex flex-wrap gap-1.5">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onPick(option)}
          className={`whitespace-nowrap rounded-md px-2.5 py-1 text-xs transition-colors ${
            active === option
              ? 'bg-accent-500 font-medium text-foreground-950'
              : 'bg-background-100 text-foreground-600 hover:bg-background-200'
          }`}
        >
          {option}
        </button>
      ))}
    </div>
  </div>
);

export function applyFilters(list: Work[], query: CatalogQuery): Work[] {
  return list
    .filter((item) => (query.genre === '全部' ? true : item.genres.includes(query.genre)))
    .filter((item) => (query.region === '全部' ? true : item.region === query.region))
    .filter((item) => {
      if (query.year === '全部') return true;
      if (query.year === '更早') return item.year < 2023;
      return String(item.year) === query.year;
    })
    .sort((a, b) => {
      if (query.sort === '评分最高') return b.rating - a.rating;
      if (query.sort === '最多关注') return b.viewsCount - a.viewsCount;
      return b.year - a.year || b.rating - a.rating;
    });
}

export default function FilterPanel({ resultCount, value, onChange, onReset }: FilterPanelProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) setOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <aside className="w-full lg:w-[248px] lg:shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between rounded-lg border border-background-200/70 bg-background-50 px-4 py-3 text-sm font-medium text-foreground-900 lg:hidden"
      >
        <span className="flex items-center gap-2">
          <i className="ri-filter-3-line text-accent-600"></i>
          筛选条件
        </span>
        <i className={`${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-500`}></i>
      </button>

      <div
        className={`mt-3 rounded-lg border border-background-200/70 bg-background-50 p-4 lg:mt-0 lg:sticky lg:top-[112px] ${
          open ? 'block' : 'hidden lg:block'
        }`}
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display text-sm font-bold text-foreground-950">筛选</h2>
          <button
            type="button"
            onClick={onReset}
            className="flex items-center gap-1 whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-accent-700"
          >
            <i className="ri-restart-line"></i>
            重置
          </button>
        </div>

        <div className="mt-4 space-y-5">
          {group('类型', 'ri-movie-line', genreOptions, value.genre, (v) => onChange({ ...value, genre: v }))}
          {group('地区', 'ri-earth-line', regionOptions, value.region, (v) => onChange({ ...value, region: v }))}
          {group('年份', 'ri-calendar-line', yearOptions, value.year, (v) => onChange({ ...value, year: v }))}
          {group('排序', 'ri-sort-desc', [...sortOptions], value.sort, (v) => onChange({ ...value, sort: v }))}
        </div>

        <div className="mt-5 rounded-md bg-accent-50 p-3">
          <p className="text-xs leading-relaxed text-accent-900/80">
            当前共有 <span className="font-bold text-accent-800">{resultCount}</span> 部作品符合条件
          </p>
        </div>

        <Link
          to="/favorites"
          className="mt-4 flex items-center justify-between rounded-md border border-background-300/70 px-3 py-2.5 text-xs text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-700"
        >
          查看我的收藏
          <i className="ri-heart-3-line"></i>
        </Link>
      </div>
    </aside>
  );
}